import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { usePagination } from "src/hooks/usePagination";
import { useOutsideClick } from "src/hooks/useOutsideClick";
import { SortingState } from "@tanstack/react-table";
import { Transition } from "@headlessui/react";
import Button from "src/features/fields/components/Button";
import Table from "src/features/fields/components/Table";
import POFilter from "src/features/fields/container/POFilter";
import { createColumnHelper } from "@tanstack/react-table";
import moment from "moment";
import FilterIcon from "src/assets/filter.svg";
import { getMarginTypeException } from "src/services/order-api";
import { queryDivisionDetails } from "../../../services/division-api";
import { poActions } from "src/context/po-slice";
import { appendParams } from "src/utils/utils";
import { getAllCompanyDetails } from "src/services/company-api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "src/components/ui/select.component";
import { Input } from "src/components";

const MarginTypeException = () => {
    const [loading, setLoading] = useState(false);
    const { marginTypeExceptionTableData, poFiltersData, marginTypeExceptionFilters } = useSelector((state: any) => state.po);
    const { pagination, onPaginationChange, pageIndex, pageSize, resetPagination } = usePagination();
    const [totalRowCount, setTotalRowCount] = useState(10);
    const [isFiltersShown, setIsFiltersShown] = useState<boolean>(false);
    const [sorting, setSorting] = useState<SortingState>();
    const [globalSearch, setGlobalSearch] = useState("");
    const [searchCategory, setSearchCategory] = useState("item_code_po");

    const dispatch = useDispatch();

    const ref = useOutsideClick(() => {
        setIsFiltersShown(false);
    });

    const searchOptions = [
        { label: "Item Code", value: "item_code_po" },
        { label: "Counter/Location Code", value: "counter_location_code" },
        { label: "Stockist Alias Code", value: "compStockistAliasCode" },
    ];

    const [_, setSearchParams] = useSearchParams();

    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor("company_name", {
            header: () => "Company Name",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("division_name", {
            header: () => "Division Name",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("module_desc", {
            header: () => "Module",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("item_code_po", {
            header: () => "Item Code",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("item_name", {
            header: () => "Item Name",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("counter_location_code", {
            header: () => "Counter/Location Code",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("compStockistAliasCode", {
            header: () => "Stockist Alias Code",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("stockist_margin_value", {
            header: () => "Margin Value",
            enableSorting: true,
            cell: (info: any) => info.getValue() ?? "-",
        }),
        columnHelper.accessor("stockist_margin_desc", {
            header: () => "Margin Description",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("payment_term_code", {
            header: () => "Payment Term Code",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("active_status", {
            header: () => "Active Status",
            enableSorting: true,
            cell: (info: any) => info.getValue() || "-",
        }),
        columnHelper.accessor("created_date", {
            header: () => "Created Date",
            enableSorting: true,
            cell: (info: any) => {
                const date = info.getValue();
                return date
                    ? moment(date).tz("Asia/Kolkata").format("DD MMM YYYY")
                    : "-";
            },
        }),
    ];

    const fetchDetails = () => {
        setLoading(true);

        setSearchParams({
            pageSize: pageSize.toString(),
            pageIndex: pageIndex.toString(),
        });

        const companyParams = appendParams(Array.isArray(marginTypeExceptionFilters?.company) ? marginTypeExceptionFilters.company : [], "company", "code");

        const divisionParams = appendParams(
            Array.isArray(marginTypeExceptionFilters?.division) ? marginTypeExceptionFilters.division : [],
            "division",
            "company_divisioncode"
        );

        let filtersQuery = [companyParams, divisionParams].join("&");

        filtersQuery.endsWith("&&") && (filtersQuery = filtersQuery.slice(0, -1));
        filtersQuery.endsWith("&") && (filtersQuery = filtersQuery.slice(0, -1));

        getMarginTypeException(
            pageIndex - 1,
            pageSize,
            filtersQuery,
            marginTypeExceptionFilters?.company?.[0]?.code || 0,
            searchCategory, globalSearch
        )
            .then((res) => {
                dispatch(poActions.setMarginTypeExceptionTableData(res?.data?.data?.data || []));
                setTotalRowCount(res?.data?.data?.totalDataCount);
            })
            .catch((err) => {
                console.log(err);
            })
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        resetPagination();
    }, [
        marginTypeExceptionFilters?.company,
        marginTypeExceptionFilters?.division,
        globalSearch
    ]);

    useEffect(() => {
        fetchDetails();
    }, [pageSize, pageIndex, sorting, globalSearch]);

    let pageCount = Math.ceil(totalRowCount / (pageSize ?? 1));
    if (totalRowCount % 10 === 0) {
        pageCount--;
    }

    let timerId: any;
    const handleSearch = (searchQuery: string) => {
        if (timerId) clearTimeout(timerId);

        timerId = setTimeout(() => {
            resetPagination();
            setGlobalSearch(searchQuery);
        }, 500);
    };

    useEffect(() => {
        getAllCompanyDetails()
            .then((res) =>
                dispatch(poActions.setPoFiltersData({ key: "company", value: res.data }))
            )
            .catch((err) => console.log(err));

        if (marginTypeExceptionFilters?.company?.length) {
            queryDivisionDetails(`/details/?fields=division_name&company_code=${marginTypeExceptionFilters?.company[0]?.code}`)
                .then((res) => dispatch(poActions.setPoFiltersData({ key: "marginTypeExceptionDivision", value: res.data })))
                .catch((err) => console.log(err));
        } else {
            dispatch(poActions.setPoFiltersData({ key: "marginTypeExceptionDivision", value: [] }));
            dispatch(poActions.setMarginTypeExceptionFilters({ key: "division", value: [] }));
        }

    }, [marginTypeExceptionFilters?.company, dispatch]);

    useEffect(() => {
        if (globalSearch?.trim()) {
            fetchDetails();
        }
    }, [searchCategory]);

    return (
        <>
            <Transition
                as={Fragment}
                show={isFiltersShown}
                enter="transition transition ease-in-out duration-200 transform"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transition  ease-in-out duration-200 transform"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
            >
                <div className="fixed right-0 inset-0 top-0 z-[500]">
                    <POFilter
                        ref={ref}
                        companyDetails={poFiltersData?.company || []}
                        divisionDetails={poFiltersData?.marginTypeExceptionDivision || []}
                        source="MarginTypeException"
                        onCloseClick={() => setIsFiltersShown(false)}
                    />
                </div>
            </Transition>

            <div className="flex justify-end float-right gap-2">

                {/* Search Box */}
                <div className="flex items-center gap-2 w-[500px] rounded-md overflow-hidden border border-gray-300 bg-white">
                    <Select value={searchCategory} onValueChange={setSearchCategory}>
                        <SelectTrigger className="w-[360px] rounded-none border-none bg-white text-gray-700 focus:ring-0 focus:outline-none focus:shadow-none">
                            <SelectValue placeholder="Search By" />
                        </SelectTrigger>
                        <SelectContent className="bg-white">
                            {searchOptions.map((option) => (
                                <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>

                    <Input
                        placeholder="Search"
                        debounceTime={500}
                        value={globalSearch}
                        onChange={(e) => handleSearch(e.target.value.trim())}
                        containerClasses="w-full px-3 py-1 text-gray-700 border-none bg-white focus:outline-none focus:ring-0 focus:shadow-none"
                    />
                </div>

                {/* Filters Button */}
                <Button
                    className="relative flex items-center gap-1 px-4 border"
                    onClick={() => setIsFiltersShown(true)}
                >
                    Filters <img src={FilterIcon} className="w-5 h-5" />
                    {marginTypeExceptionFilters?.filterCount > 0 && (
                        <span className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 text-xs text-white bg-red-500 rounded-full">
                            {marginTypeExceptionFilters?.filterCount}
                        </span>
                    )}
                </Button>

                {/* Retrieve Button */}
                <Button
                    onClick={fetchDetails}
                    loaderSize="small"
                    className="h-auto py-2 border"
                >
                    Retrieve
                </Button>
            </div>

            <Table
                title="Margin Type Exception Requests"
                subtitle="View and manage all your margin type exception requests."
                columns={columns}
                data={marginTypeExceptionTableData ?? []}
                isLoading={loading}
                isGlobalSearch={true}
                manualPagination={true}
                pageCount={pageCount}
                pagination={pagination}
                onPaginationChange={onPaginationChange}
                sorting={sorting}
                onSortingChange={setSorting}
                manualSorting={true}
                showSearch={false}
                onGlobalSearch={handleSearch}
            />
        </>
    );
};

export default MarginTypeException;
